import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    category: 'CUET UG',
    items: [
      { q: 'What is CUET UG?', a: 'CUET UG (Common University Entrance Test for Undergraduates) is a national-level entrance exam conducted by NTA for admission to UG programs at Central, State, Deemed, and Private Universities across India.' },
      { q: 'Is CUET UG mandatory for all Central Universities?', a: 'Yes, all Central Universities are required to use CUET UG scores for undergraduate admissions. State and Private Universities may also opt in.' },
      { q: 'How many subjects can I choose in CUET UG?', a: 'You can choose a maximum of 6 subjects: up to 2 language papers, up to 6 domain subjects, and 1 General Test. The combination depends on the requirements of your target universities and programs.' },
      { q: 'Is there negative marking in CUET UG?', a: 'Yes — +5 marks for correct answers and -1 for incorrect answers. Unattempted questions carry no penalty.' },
    ]
  },
  {
    category: 'CUET PG',
    items: [
      { q: 'Is CUET PG counselling centralised?', a: 'No. CUET PG is decentralised — NTA conducts the exam and releases scores, but each university manages its own counselling, merit lists, and admissions independently. You must register separately on each university\'s portal.' },
      { q: 'Can I apply to a university not selected in the NTA form?', a: 'Many universities allow counselling registration even if not selected at the NTA application stage, provided you meet eligibility. Always verify on the specific university portal.' },
      { q: 'What documents are required for CUET PG counselling?', a: 'Typically: CUET PG scorecard & admit card, Class 10 & 12 marksheets, all semester marksheets of your UG degree, category certificate (if applicable), transfer/migration certificates, government photo ID, and a recent passport photo.' },
    ]
  },
  {
    category: 'UGC NET / JRF',
    items: [
      { q: 'What is the difference between NET and JRF?', a: 'Qualifying UGC NET makes you eligible for Assistant Professor positions. Candidates who score in the top percentile also receive JRF (Junior Research Fellowship) — a monthly stipend for pursuing PhD research.' },
      { q: 'How many times is UGC NET conducted per year?', a: 'UGC NET is conducted twice a year — typically in June and December — by NTA.' },
      { q: 'Is Paper 1 the same for all subjects?', a: 'Yes. Paper 1 (Teaching & Research Aptitude) is common for all candidates regardless of their subject specialisation. It covers 9 units including Teaching Aptitude, Research Aptitude, Logical Reasoning, ICT, and more.' },
      { q: 'Is there negative marking in UGC NET?', a: 'No. UGC NET has no negative marking. Each correct answer awards +2 marks; incorrect or unattempted answers receive 0.' },
    ]
  },
  {
    category: 'BESST Platform',
    items: [
      { q: 'Are the mock tests free?', a: 'Yes — CUET UG mock tests, UGC NET Paper 1 mock tests, and current affairs tests are free with basic registration. Premium subscription plans unlock unlimited tests, study materials, and live counselling.' },
      { q: 'How do I subscribe?', a: 'Go to the Subscriptions page, choose your plan (1-month, 3-month, or 6-month), select your subjects, and complete payment via Razorpay. Your subscription activates immediately.' },
      { q: 'Can I take the tests on my phone?', a: 'Yes. The test engine is fully mobile-responsive. The question palette moves to the bottom on mobile for easy access.' },
      { q: 'What happens after my subscription expires?', a: 'Your subscription pack expires after the validity period. Past results and performance data remain accessible. You can renew or purchase a new subscription at any time.' },
      { q: 'Is there a refund policy?', a: 'Please contact our support team at admissions@besst.in for queries related to refunds. We review each case individually.' },
      { q: 'How do I contact support?', a: 'Email us at admissions@besst.in, reach us via WhatsApp, or use the Contact page. Our team typically responds within 24 hours.' },
    ]
  },
];

const FaqPage = () => {
  const [open, setOpen] = useState({});
  const toggle = (cat, i) => setOpen(prev => ({ ...prev, [`${cat}-${i}`]: !prev[`${cat}-${i}`] }));

  return (
    <>
      <Helmet>
        <title>FAQ – Frequently Asked Questions | BESST</title>
        <meta name="description" content="Answers to common questions about CUET UG, CUET PG, UGC NET/JRF, subscriptions, and the BESST platform." />
        <link rel="canonical" href="https://besst.in/faq" />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">

          {/* Header */}
          <div className="text-center mb-12">
            <div className="hero-badge mx-auto mb-4">❓ Help Centre</div>
            <h1 className="text-4xl lg:text-5xl font-extrabold gradient-text mb-4">Frequently Asked Questions</h1>
            <p className="text-xl text-text-muted max-w-2xl mx-auto">
              Quick answers about CUET UG, CUET PG, UGC NET/JRF, and the BESST platform.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-10">
            {/* FAQ Content */}
            <div className="flex-1 space-y-10">
              {faqs.map(section => (
                <div key={section.category}>
                  <h2 className="text-xl font-bold text-text-light mb-4 flex items-center gap-2">
                    <span className="w-2 h-6 bg-accent-gold rounded-full inline-block"></span>
                    {section.category}
                  </h2>
                  <div className="space-y-3">
                    {section.items.map((faq, i) => (
                      <div key={i} className="faq-item">
                        <button
                          className="faq-question w-full text-left"
                          onClick={() => toggle(section.category, i)}
                        >
                          <span>{faq.q}</span>
                          <svg
                            className={`w-5 h-5 text-accent-gold flex-shrink-0 transition-transform ${open[`${section.category}-${i}`] ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {open[`${section.category}-${i}`] && (
                          <p className="faq-answer">{faq.a}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Still have questions */}
              <div className="glass-card text-center bg-gradient-to-br from-primary/10 to-accent-gold/10">
                <div className="text-4xl mb-3">💬</div>
                <h3 className="text-xl font-bold text-text-light mb-2">Still have questions?</h3>
                <p className="text-text-muted mb-4">Our team is happy to help. Reach out via the contact page or WhatsApp.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/contact" className="btn-primary px-6 py-3">Contact Us</Link>
                  <a href="https://api.whatsapp.com/send/?phone=918822403212&text=Hello+BESST%2C+I+have+a+query.&type=phone_number&app_absent=0" target="_blank" rel="noreferrer"
                    className="btn-secondary px-6 py-3">💬 WhatsApp</a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-28 space-y-6">
                {/* Jump to section */}
                <div className="glass-card">
                  <h3 className="text-lg font-bold text-text-light mb-4">Jump to Section</h3>
                  <div className="space-y-2">
                    {faqs.map(s => (
                      <button key={s.category}
                        onClick={() => document.querySelector(`[data-section="${s.category}"]`)?.scrollIntoView({ behavior: 'smooth' })}
                        className="w-full text-left flex items-center gap-2 p-2 rounded-lg text-text-muted hover:text-primary hover:bg-primary/10 transition-colors text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {s.category}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick links */}
                <div className="glass-card">
                  <h3 className="text-lg font-bold text-text-light mb-4">Helpful Links</h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Free Mock Tests', path: '/cuet-ug-mock-test' },
                      { label: 'Study Materials', path: '/study-materials' },
                      { label: 'Subscription Plans', path: '/subscriptions' },
                      { label: 'Contact Us', path: '/contact' },
                      { label: 'Privacy Policy', path: '/privacy-policy' },
                      { label: 'Terms of Use', path: '/terms' },
                    ].map(l => (
                      <Link key={l.path} to={l.path}
                        className="flex items-center gap-2 p-2 rounded-lg text-text-muted hover:text-primary hover:bg-primary/10 transition-colors text-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default FaqPage;
